import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import {
    BudgetPurpose,
    UpsertBudgetCategoryGroupDocument,
} from '../../graphql/generated';
import { BudgetCategoryGroupWithAverage } from './types';

interface BudgetCategoryGroupMutationProps {
    budgetCategoryGroup: BudgetCategoryGroupWithAverage;
    budgetPurpose: BudgetPurpose;
    onError: (error: unknown) => void;
}

interface UpdateAttributes {
    enabled?: boolean;
    amount?: number;
    budgetPurpose?: BudgetPurpose;
}

export const useUpsertBudgetCategoryGroup = ({
    budgetCategoryGroup,
    budgetPurpose: initialBudgetPurpose,
    onError,
}: BudgetCategoryGroupMutationProps) => {
    const [enabled, setEnabled] = useState(budgetCategoryGroup.enabled);
    const [amount, setAmount] = useState(budgetCategoryGroup.amount);
    const [budgetPurpose, setBudgetPurpose] = useState(initialBudgetPurpose);

    const [upsertBudgetCategoryGroup] = useMutation(
        UpsertBudgetCategoryGroupDocument
    );

    // Keep states in sync with external changes
    useEffect(() => {
        setEnabled(budgetCategoryGroup.enabled);
        setAmount(budgetCategoryGroup.amount);
        setBudgetPurpose(initialBudgetPurpose);
    }, [
        budgetCategoryGroup.enabled,
        budgetCategoryGroup.amount,
        initialBudgetPurpose,
    ]);

    const updateAttributes = async (attributes: UpdateAttributes) => {
        const originalEnabled = enabled;
        const originalAmount = amount;
        const originalBudgetPurpose = budgetPurpose;

        if (attributes.enabled !== undefined) setEnabled(attributes.enabled);
        if (attributes.amount !== undefined) setAmount(attributes.amount);
        if (attributes.budgetPurpose !== undefined)
            setBudgetPurpose(attributes.budgetPurpose);

        try {
            await upsertBudgetCategoryGroup({
                variables: {
                    input: {
                        budgetCategoryGroup: {
                            amount:
                                attributes.amount || budgetCategoryGroup.amount,
                            enabled: attributes.enabled,
                            categoryGroupId:
                                budgetCategoryGroup.categoryGroup.id,
                            budgetPurpose:
                                attributes.budgetPurpose || budgetPurpose,
                        },
                    },
                },
            });
        } catch (e) {
            onError(e);
            // Revert local state on error
            setEnabled(originalEnabled);
            setAmount(originalAmount);
            setBudgetPurpose(originalBudgetPurpose);
        }
    };

    return {
        enabled,
        amount,
        budgetPurpose,
        updateAttributes,
    };
};
